import { useState } from "react";
import { ThumbsUp, ThumbsDown, Sparkles, Quote, ExternalLink } from "lucide-react";
import type {
  ConversationRetrievalItem,
  EntityAwarePayload,
} from "../../lib/api";
import { api } from "../../lib/api";
import { Markdown } from "../../components/Markdown";
import { SourcesAffordance } from "./SourcesAffordance";

export interface DisplayMessage {
  id: string;
  role: "user" | "assistant" | "pending";
  content: string;
  retrieval?: ConversationRetrievalItem[];
  /** Structured entity-aware payload (count/dossier/who/where/etc).
   *  When present, an EntityAwareChips strip renders below the bubble
   *  with click-through citations to the source packet/event. */
  entityAware?: EntityAwarePayload | null;
  rating?: number | null;
  /** When true, render the loading shimmer skeleton in place of the
   *  content. The composer flips this off as soon as the response
   *  starts streaming in. */
  pending?: boolean;
}

/** One turn in the chat stream. User messages right-aligned with a
 *  neutral surface; Memso replies left-aligned with the deep-blue
 *  palette + orange accent that carries the brand. */
export function MessageBubble({ message }: { message: DisplayMessage }) {
  if (message.role === "user") {
    return <UserBubble content={message.content} />;
  }
  return <MemsoBubble message={message} />;
}

function UserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end animate-fadeInUp">
      <div className="max-w-[85%] sm:max-w-[75%] rounded-2xl rounded-br-md bg-blue-600 dark:bg-blue-500 text-white px-4 py-2.5 shadow-sm">
        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
          {content}
        </p>
      </div>
    </div>
  );
}

function MemsoBubble({ message }: { message: DisplayMessage }) {
  return (
    <div className="flex justify-start animate-fadeInUp">
      <div className="max-w-[92%] sm:max-w-[85%] flex gap-2.5">
        <div className="flex-shrink-0 inline-flex w-8 h-8 rounded-xl bg-gradient-to-br from-blue-700 via-blue-600 to-orange-500 items-center justify-center text-white shadow-sm mt-1">
          <Sparkles className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1 rounded-2xl rounded-tl-md bg-white/85 dark:bg-slate-800/70 border border-slate-200/70 dark:border-slate-700/60 backdrop-blur px-4 py-3 shadow-sm">
          {message.pending ? (
            <PendingShimmer />
          ) : (
            <>
              <Markdown citations>{message.content}</Markdown>
              {message.entityAware && (
                <EntityAwareChips payload={message.entityAware} />
              )}
              {message.retrieval && (
                <SourcesAffordance items={message.retrieval} />
              )}
              {!message.pending && message.id && !message.id.startsWith("tmp_") && (
                <FeedbackRow
                  messageId={message.id}
                  initial={message.rating ?? null}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PendingShimmer() {
  return (
    <div className="space-y-2 py-1">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-300">
        <span className="inline-flex gap-0.5" aria-hidden>
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: "240ms" }} />
        </span>
        Memso is weaving an answer&hellip;
      </div>
      <div className="h-3 rounded-full bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 animate-shimmer w-3/4" />
      <div className="h-3 rounded-full bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 animate-shimmer w-5/6" />
      <div className="h-3 rounded-full bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 animate-shimmer w-2/3" />
    </div>
  );
}

/** Chip row that renders entity-aware citations (one per backing
 *  packet / engagement / conversation summary / place resolution).
 *  Click-through routes to the appropriate hub page so the user can
 *  read the source directly. */
function EntityAwareChips({ payload }: { payload: EntityAwarePayload }) {
  const { citations, resolved, intent, anchors } = payload;
  if (!citations || citations.length === 0) {
    // Even with no citations, surface the resolved anchors as a
    // breadcrumb so the user can pivot into the deeper page.
    if (!resolved || resolved.length === 0) return null;
  }
  const resolvedChip = resolved && resolved.length > 0 ? resolved[0] : null;
  const anchorLabel = anchors && anchors.length > 0 ? anchors[0].label : null;
  return (
    <div className="mt-3 space-y-2 border-t border-slate-200/60 dark:border-slate-700/50 pt-2">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
        <Sparkles className="w-3 h-3" />
        <span>
          {intent} answer
          {resolvedChip ? (
            <>
              {" · "}
              <a
                href={anchorPivotUrl(resolvedChip)}
                className="text-blue-600 dark:text-blue-300 hover:underline"
              >
                {resolvedChip.display_name}
              </a>
            </>
          ) : anchorLabel ? (
            <> {" · "}{anchorLabel}</>
          ) : null}
        </span>
      </div>
      {citations && citations.length > 0 && (
        <ul className="space-y-1">
          {citations.slice(0, 10).map((c, ci) => (
            <li
              key={`${c.ref_kind}_${c.ref_id}_${ci}`}
              className="text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-2"
            >
              <Quote className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="font-bold mr-1">[{ci + 1}]</span>
                <a
                  href={citationHref(c)}
                  className="text-blue-600 dark:text-blue-300 hover:underline inline-flex items-center gap-0.5"
                >
                  {citationKindLabel(c.ref_kind)}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
                {c.date && (
                  <span className="text-slate-400 dark:text-slate-500 ml-1">
                    {String(c.date).slice(0, 10)}
                  </span>
                )}
                <span className="ml-1 break-words">{c.label}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function anchorPivotUrl(r: { id: string; type: string; display_name: string }): string {
  const t = r.type.toLowerCase();
  if (t === "person") return `/people/${encodeURIComponent(r.id)}`;
  if (t === "place") return `/places/${encodeURIComponent(r.id)}`;
  return `/explore?q=${encodeURIComponent(r.display_name)}`;
}

function citationHref(c: { ref_kind: string; ref_id: string }): string {
  switch (c.ref_kind) {
    case "packet":
      return `/explore?packet=${encodeURIComponent(c.ref_id)}`;
    case "engagement":
      return `/needs-attention?engagement=${encodeURIComponent(c.ref_id)}`;
    case "conversation_summary":
      return `/conversations?thread=${encodeURIComponent(c.ref_id)}`;
    case "place_resolution":
      return `/explore?place=${encodeURIComponent(c.ref_id)}`;
    case "graph_edge":
      return `/explore?edge=${encodeURIComponent(c.ref_id)}`;
    default:
      return `/explore?ref=${encodeURIComponent(c.ref_id)}`;
  }
}

function citationKindLabel(kind: string): string {
  switch (kind) {
    case "packet":
      return "Packet";
    case "engagement":
      return "Engagement";
    case "conversation_summary":
      return "Thread";
    case "place_resolution":
      return "Visit";
    case "graph_edge":
      return "Edge";
    default:
      return kind;
  }
}

function FeedbackRow({
  messageId,
  initial,
}: {
  messageId: string;
  initial: number | null;
}) {
  const [rating, setRating] = useState<number | null>(initial);
  const [busy, setBusy] = useState(false);
  const send = async (next: 1 | -1) => {
    if (busy) return;
    setBusy(true);
    const resolved = rating === next ? 0 : next;
    setRating(resolved);
    try {
      await api.conversationFeedback({ message_id: messageId, rating: resolved as -1 | 0 | 1 });
    } catch {
      setRating(initial);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 flex items-center gap-1">
      <button
        type="button"
        onClick={() => send(1)}
        aria-label="Helpful"
        aria-pressed={rating === 1}
        className={`inline-flex w-7 h-7 items-center justify-center rounded-lg transition-colors ${
          rating === 1
            ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300"
            : "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
        }`}
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => send(-1)}
        aria-label="Not helpful"
        aria-pressed={rating === -1}
        className={`inline-flex w-7 h-7 items-center justify-center rounded-lg transition-colors ${
          rating === -1
            ? "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300"
            : "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
        }`}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

